import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartDataPoint } from './types';

const DEFAULT_COLORS = ['#6366f1', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

const RADIAN = Math.PI / 180;

interface PieLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  value?: number;
}

function renderSliceLabel({ cx, cy, midAngle, innerRadius, outerRadius, value }: PieLabelProps) {
  if (
    !value ||
    cx == null ||
    cy == null ||
    midAngle == null ||
    innerRadius == null ||
    outerRadius == null
  )
    return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {value}
    </text>
  );
}

interface PieChartProps {
  data: ChartDataPoint[];
  hideLegend?: boolean;
  showLabel?: boolean;
}

/**
 * Pie chart for categorical data. Uses `entry.color` when present, otherwise
 * cycles through a default 6-color palette. Includes legend and tooltip.
 * Fills its parent container via `ResponsiveContainer`.
 *
 * @example
 * <PieChart
 *   data={[{ name: 'Open', value: 40 }, { name: 'Done', value: 60 }]}
 * />
 */
export function PieChart({ data, hideLegend = false, showLabel = false }: PieChartProps) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsPieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="45%"
          outerRadius="82%"
          label={showLabel ? renderSliceLabel : false}
          labelLine={false}
        >
          {data.map((entry, index) => (
            <Cell
              key={entry.name}
              fill={entry.color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) =>
            typeof value === 'number' ? value.toLocaleString() : String(value ?? '')
          }
        />
        {!hideLegend && (
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
        )}
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}
